import { domainAliases } from "./domainAliases.js";
/**
 * Helper to build tenant-aware URLs that preserve edition prefixes.
 * 
 * @param {Object} options
 * @param {Object} options.publication - The resolved publication object
 * @param {string} options.path - The target path (e.g. '/story', '/category/news')
 * @returns {string} The fully formed absolute path including the edition prefix
 */
function parseGeneral(publication) {
  const value = publication?.settings?.general;
  if (!value) return {};
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return {}; }
}

function normalizeDomain(value) {
  return String(value || '').trim().replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

function domainHost(value) {
  return normalizeDomain(value).split(':')[0].toLowerCase();
}

export function findPublicationNetworkRoot(publication, publications = []) {
  const routePrefix = String(parseGeneral(publication).routePrefix || '').trim();
  if (!routePrefix) return publication;
  return publications.find(candidate => {
    if (!candidate || candidate.id === publication.id || !candidate.primaryDomain) return false;
    if (candidate.templateId !== publication.templateId || candidate.ownerCompany !== publication.ownerCompany) return false;
    if (String(parseGeneral(candidate).routePrefix || '').trim()) return false;
    return String(publication.name || '').startsWith(String(candidate.name || ''));
  }) || publication;
}

export function publicationDomainHosts(publication, publications = []) {
  const root = findPublicationNetworkRoot(publication, publications);
  return [...new Set([
    publication?.primaryDomain,
    publication?.previewDomain,
    publication?.devDomain,
    ...(root?.id !== publication?.id ? [root.primaryDomain, root.previewDomain, root.devDomain] : [])
  ].map(domainHost).filter(Boolean))];
}

export function publicationOwnsUrl(publication, value, publications = []) {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) && publicationDomainHosts(publication, publications).includes(url.hostname.toLowerCase());
  } catch {
    return false;
  }
}

export function validatePublicationOrigin(value, publication, publications = []) {
  const candidate = String(value || "").trim();
  if (!candidate) return "";
  let url;
  try {
    url = new URL(candidate);
  } catch {
    throw new Error("Canonical origin must be a valid URL");
  }
  if (url.protocol !== "https:" && !(url.protocol === "http:" && url.hostname === "localhost")) {
    throw new Error("Canonical origin must use HTTPS");
  }
  const root = findPublicationNetworkRoot(publication, publications);
  const canonicalHosts = [root?.primaryDomain || publication?.primaryDomain]
    .map(domainHost)
    .filter(Boolean);
  const isLocalDevelopment = url.protocol === "http:" && url.hostname === "localhost" && canonicalHosts.includes("localhost");
  if ((!canonicalHosts.includes(url.hostname.toLowerCase()) && !isLocalDevelopment) || !publicationOwnsUrl(publication, url.href, publications)) {
    throw new Error("Canonical origin does not belong to this publication");
  }
  return url.origin;
}

export function resolvePublicationOrigin(publication, config = {}, publications = []) {
  const configured = String(config?.siteUrl || '').trim();
  if (configured) {
    try {
      const url = new URL(configured);
      const supported = ['http:', 'https:'].includes(url.protocol);
      // Stored SEO origins pass the publication-scoped validator on write. Callers
      // with the publication registry still receive a fresh ownership check.
      const root = findPublicationNetworkRoot(publication, publications);
      const canonicalHost = domainHost(root?.primaryDomain || publication?.primaryDomain);
      if (supported && (!Object.values(domainAliases()).includes(publication?.id) || url.hostname.toLowerCase() === canonicalHost) && (publications.length === 0 || (url.hostname.toLowerCase() === canonicalHost && publicationOwnsUrl(publication, configured, publications)))) return url.origin;
    } catch {}
  }
  const root = findPublicationNetworkRoot(publication, publications);
  const domain = normalizeDomain(root?.primaryDomain || publication?.primaryDomain || root?.previewDomain || publication?.previewDomain || root?.devDomain || publication?.devDomain);
  if (!domain) return '';
  const protocol = domain.includes(':') || /^(localhost|127\.)/.test(domain) ? 'http' : 'https';
  return `${protocol}://${domain}`;
}

export function buildPublicationUrl({ publication, path, absolute = false, fallbackDomain = '', publications = [] }) {
  if (!path) path = '/';
  if (!path.startsWith('/')) path = `/${path}`;

  let prefix = '';
  const generalSettings = parseGeneral(publication);
  if (generalSettings.routePrefix) {
    prefix = `/${generalSettings.routePrefix}`;
  }

  let finalPath = '';
  // If path is root '/', return just the prefix or '/'
  if (path === '/') {
    finalPath = prefix ? `${prefix}/` : '/';
  } else if (prefix && (path === prefix || path.startsWith(`${prefix}/`))) {
    finalPath = path;
  } else {
    finalPath = `${prefix}${path}`;
  }

  if (absolute) {
    let base = fallbackDomain || resolvePublicationOrigin(publication, publication?.settings?.seo || {}, publications);
    if (!base) return finalPath;
    // ensure base doesn't end with slash if finalPath starts with it
    if (base.endsWith('/') && finalPath.startsWith('/')) {
      base = base.slice(0, -1);
    } else if (!base.endsWith('/') && !finalPath.startsWith('/')) {
      base = `${base}/`;
    }
    return `${base}${finalPath}`;
  }
  
  return finalPath;
}
